/**
 * TransactionsTab — 2-column ledger view, rendered 1:1 from
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/project-details.html
 *   (lines 154-416 — the `ptab-transactions` block).
 *
 * Layout:
 *   LEFT (1fr) — Financial Ledger card: search + type filter +
 *     filters button → 5-column desktop table → pagination footer
 *   RIGHT (360px) — Payment In/Out CTA pair, KPI cards (Received,
 *     Spent, Net), Pending Approvals card, Client Payments card
 *
 * Live data via `useProjectTransactions(orgId, projectId)`.
 * Schema parity with the mobile transactions collection.
 */
import { useMemo, useState } from 'react';

import { canFinance, isSubmitOnlyTxnRole } from '@shared/perms';

import { AddAdvanceBillSlideOver } from '@/components/AddAdvanceBillSlideOver';
import { CloseAdvanceSlideOver } from '@/components/CloseAdvanceSlideOver';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import type { ProjectListItem } from '@/lib/useProjects';
import {
  advanceStatusLabel,
  advanceStatusTone,
  categoryLabel,
  computeAdvanceBalance,
  paymentMethodLabel,
  unapplyAdvanceSettlement,
  type PaymentMethod,
  type TransactionCategory,
} from '@/lib/transactions';
import { useAdvanceChildren } from '@/lib/useAdvances';
import {
  isCountedInTotals,
  normaliseTxType,
  txDateMs,
  useProjectTransactions,
  type TransactionItem,
} from '@/lib/useProjectTransactions';

// ───── Helpers ─────

function compactInr(n: number): string {
  if (!n) return '0';
  const abs = Math.abs(n);
  if (abs >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (abs >= 100000)   return `${(n / 100000).toFixed(1)}L`;
  if (abs >= 1000)     return `${Math.round(n / 1000)}K`;
  return n.toLocaleString('en-IN');
}

function formatInrFull(n: number): string {
  return n.toLocaleString('en-IN');
}

function dayShort(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
}

function yearOf(ms: number | null): string {
  if (!ms) return '';
  return String(new Date(ms).getFullYear());
}

function partyInitials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
}

const AVATAR_PALETTE = [
  { bg: '#FEF3C7', fg: '#D97706' }, // amber
  { bg: '#E0E7FF', fg: '#4338CA' }, // indigo
  { bg: '#F3E8FF', fg: '#7C3AED' }, // purple
  { bg: '#DBEAFE', fg: '#2563EB' }, // blue
  { bg: '#D1FAE5', fg: '#059669' }, // green
  { bg: '#FEE2E2', fg: '#DC2626' }, // red
];
function pickAvatarColors(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

// ───── Filters ─────

type TypeFilter = 'all' | 'payment_in' | 'payment_out';

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'all',          label: 'All Types' },
  { value: 'payment_in',   label: 'Payment In' },
  { value: 'payment_out',  label: 'Payment Out' },
];

// ───── Page ─────

const PAGE_SIZE = 25;

export function TransactionsTab({
  project,
  onOpenTransaction,
  onCreateTransaction,
}: {
  project: ProjectListItem;
  onOpenTransaction: (id: string) => void;
  onCreateTransaction: (type: 'payment_in' | 'payment_out') => void;
}) {
  const { transactions, loading, error } = useProjectTransactions(
    project.orgId ?? null,
    project.id,
  );

  // Transaction-write capability gate per the role matrix
  // (`docs/roles-and-permissions.md` — Transactions row). SA, Admin,
  // Manager, Accountant can post full transactions (including
  // approve/reject pending submissions). Site Engineer + Supervisor
  // can submit payment_out rows pending approval. Designer / Viewer /
  // Client are READ-ONLY and must not see the Payment buttons.
  // Firestore rules enforce this server-side; this hides the affordance.
  const { role: orgRole } = useCurrentOrgRole();
  const canPostTxn = canFinance(orgRole);
  // Site Engineer + Supervisor can submit transactions for
  // approval but not post directly. `isSubmitOnlyTxnRole` is the
  // single source of truth for that role bucket.
  const canSubmitTxn = isSubmitOnlyTxnRole(orgRole);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<TransactionCategory | 'all'>('all');
  const [methodFilter, setMethodFilter] = useState<PaymentMethod | 'all'>('all');
  const [page, setPage] = useState(1);

  // Which parent advance rows are expanded to show their children
  // inline. Map<advanceId, true>. State is local — no URL persistence
  // (cheap to re-expand on page revisit; saves search-param noise).
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpanded = (id: string) =>
    setExpanded((m) => ({ ...m, [id]: !m[id] }));

  // Advance slide-over state. We host both here (instead of plumbing
  // callbacks up to ProjectDetail) so the wiring is local + the
  // slide-overs auto-refresh via the live snapshot.
  const [addBillTo, setAddBillTo] = useState<TransactionItem | null>(null);
  const [closeAdvanceFor, setCloseAdvanceFor] = useState<TransactionItem | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return transactions.filter((t) => {
      const kind = normaliseTxType(t.type);
      if (typeFilter === 'payment_in' && kind !== 'in') return false;
      if (typeFilter === 'payment_out' && kind !== 'out') return false;
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      if (methodFilter !== 'all' && t.paymentMethod !== methodFilter) return false;
      if (q) {
        const blob = [
          t.partyName ?? '',
          t.description ?? '',
          t.referenceNumber ?? '',
          categoryLabel(t.category as TransactionCategory) ?? '',
          paymentMethodLabel(t.paymentMethod as PaymentMethod) ?? '',
        ].join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [transactions, search, typeFilter, categoryFilter, methodFilter]);

  const activeFilterCount =
    (categoryFilter !== 'all' ? 1 : 0) +
    (methodFilter !== 'all' ? 1 : 0);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  useMemo(() => setPage(1), [search, typeFilter, categoryFilter, methodFilter]);

  // ── Totals (only `posted` count) ──
  const totals = useMemo(() => {
    let income = 0;
    let expense = 0;
    let pendingCount = 0;
    let pendingAmount = 0;
    for (const t of transactions) {
      const k = normaliseTxType(t.type);
      if (!k) continue;
      const counted = isCountedInTotals(t);
      if (counted) {
        if (k === 'in') income += t.amount ?? 0;
        else expense += t.amount ?? 0;
      } else if (t.workflowStatus === 'pending_approval') {
        pendingCount += 1;
        if (k === 'out') pendingAmount += t.amount ?? 0;
      }
    }
    return {
      income,
      expense,
      net: income - expense,
      pendingCount,
      pendingAmount,
    };
  }, [transactions]);

  // ── Client Payments (treats all payment_in as client payments) ──
  const budget = project.value ?? 0;
  const collectedPct = budget > 0 ? Math.min(100, Math.round((totals.income / budget) * 100)) : 0;
  const balancePending = Math.max(0, budget - totals.income);

  const pendingTxns = useMemo(
    () => transactions.filter((t) => t.workflowStatus === 'pending_approval').slice(0, 5),
    [transactions],
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 360px',
        gap: 24,
        alignItems: 'start',
      }}
    >
      {/* ─── LEFT: Ledger card ─── */}
      <div
        className="card"
        style={{
          padding: 0,
          border: 'none',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        {/* Table header — search + type select + Filters button */}
        <div
          style={{
            padding: '12px 16px',
            background: '#fff',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <i
                className="ph ph-magnifying-glass"
                style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-tertiary)',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="search"
                placeholder="Search transactions…"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                style={{
                  padding: '8px 12px 8px 36px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 13,
                  width: 250,
                  maxWidth: '100%',
                  background: '#FAFAFA',
                  fontFamily: 'inherit',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.currentTarget.value as TypeFilter)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
                background: '#FAFAFA',
                color: 'var(--text-secondary)',
                fontFamily: 'inherit',
              }}
            >
              {TYPE_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <FiltersButton
              activeCount={activeFilterCount}
              category={categoryFilter}
              method={methodFilter}
              onCategory={setCategoryFilter}
              onMethod={setMethodFilter}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table
            className="desktop-txn-table"
            style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}
          >
            <thead>
              <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                {['Date', 'Party & Description', 'Category', 'Method / Ref'].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '8px 16px',
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
                <th
                  style={{
                    padding: '8px 16px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'right',
                  }}
                >
                  Amount (₹)
                </th>
                <th style={{ padding: '8px 16px', width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Loading transactions…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} style={{ padding: 48, textAlign: 'center', color: 'var(--critical)' }}>
                    Couldn't load: {error}
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)' }}>
                    {transactions.length === 0
                      ? 'No transactions yet on this project.'
                      : 'No transactions match the current filter.'}
                  </td>
                </tr>
              ) : (
                pageRows.map((t) =>
                  t.category === 'staff_advance' ? (
                    <AdvanceParentRow
                      key={t.id}
                      txn={t}
                      expanded={!!expanded[t.id]}
                      onToggle={() => toggleExpanded(t.id)}
                      onOpen={() => onOpenTransaction(t.id)}
                      onAddBill={() => setAddBillTo(t)}
                      onClose={() => setCloseAdvanceFor(t)}
                    />
                  ) : (
                    <TxnRow
                      key={t.id}
                      txn={t}
                      onClick={() => onOpenTransaction(t.id)}
                    />
                  ),
                )
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div
          style={{
            padding: '8px 16px',
            background: '#FAFAFA',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {filtered.length === 0
              ? 'Showing 0 entries'
              : `Showing ${pageStart + 1} to ${Math.min(pageStart + PAGE_SIZE, filtered.length)} of ${filtered.length} entries`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn-secondary"
              style={{ height: 28, padding: '0 8px' }}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="ph ph-caret-left" />
            </button>
            <button
              type="button"
              className="btn-secondary"
              style={{ height: 28, padding: '0 8px' }}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="ph ph-caret-right" />
            </button>
          </div>
        </div>
      </div>

      {/* ─── RIGHT: Sidebar ─── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Action buttons — gated per role matrix.
            - SA / Admin / Accountant: both Payment In + Payment Out
            - Site Engineer / Supervisor: Payment Out only (lands pending)
            - Manager / Designer / Viewer / Client: nothing (read-only) */}
        {canPostTxn ? (
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              className="btn-secondary"
              style={{
                flex: 1,
                justifyContent: 'center',
                color: 'var(--success)',
                borderColor: '#D1FAE5',
                background: '#ECFDF5',
                fontWeight: 600,
              }}
              onClick={() => onCreateTransaction('payment_in')}
            >
              <i className="ph-bold ph-arrow-down-left" /> Payment In
            </button>
            <button
              type="button"
              className="btn-primary"
              style={{
                flex: 1,
                justifyContent: 'center',
                background: 'var(--critical)',
                borderColor: 'var(--critical)',
                fontWeight: 600,
              }}
              onClick={() => onCreateTransaction('payment_out')}
            >
              <i className="ph-bold ph-arrow-up-right" /> Payment Out
            </button>
          </div>
        ) : canSubmitTxn ? (
          <button
            type="button"
            className="btn-primary"
            style={{
              justifyContent: 'center',
              background: 'var(--critical)',
              borderColor: 'var(--critical)',
              fontWeight: 600,
            }}
            onClick={() => onCreateTransaction('payment_out')}
          >
            <i className="ph-bold ph-arrow-up-right" /> Submit Payment Out
          </button>
        ) : null}

        {/* KPI cards */}
        <div style={{ display: 'flex', gap: 12 }}>
          <KpiCard
            label="Received"
            value={totals.income}
            borderColor="var(--success)"
          />
          <KpiCard
            label="Spent"
            value={totals.expense}
            borderColor="var(--critical)"
          />
        </div>
        <div
          className="card"
          style={{
            padding: '12px 16px',
            borderLeft: '4px solid var(--brand)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            background: 'linear-gradient(90deg, #ffffff 0%, #fafafa 100%)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Net Balance
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: totals.net >= 0 ? 'var(--text-primary)' : 'var(--critical)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            ₹{compactInr(totals.net).replace(/[KLM]r?$/i, '')}
            <span style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>
              {compactInr(totals.net).match(/[KLM]r?$/i)?.[0] ?? ''}
            </span>
          </div>
        </div>

        {/* Pending Approvals */}
        <div className="card">
          <div className="card-header" style={{ paddingBottom: 12 }}>
            <div className="card-title">Pending Approvals</div>
            <span
              className="card-count"
              style={{ background: '#FEF3C7', color: '#D97706' }}
            >
              {totals.pendingCount}
            </span>
          </div>
          {pendingTxns.length === 0 ? (
            <div style={{ padding: '12px 0', fontSize: 12, color: 'var(--text-tertiary)' }}>
              Nothing waiting on you.
            </div>
          ) : (
            <ul className="attn-list" style={{ margin: 0, borderTop: '1px solid var(--border)' }}>
              {pendingTxns.map((t) => {
                const k = normaliseTxType(t.type);
                const amt = `${k === 'out' ? '-' : '+'}₹${compactInr(t.amount ?? 0)}`;
                return (
                  <li className="attn-item" key={t.id} style={{ borderBottom: 'none' }}>
                    <div
                      className="attn-icon quote"
                      style={{ background: '#FEF3C7', color: '#D97706', borderRadius: '50%' }}
                    >
                      <i className="ph ph-receipt" />
                    </div>
                    <div className="attn-body">
                      <div className="attn-desc" style={{ color: 'var(--text-primary)' }}>
                        {t.description?.trim() || (k === 'out' ? 'Expense' : 'Payment In')}
                      </div>
                      <div className="attn-meta">
                        <span
                          className="tn"
                          style={{
                            color: k === 'out' ? 'var(--critical)' : 'var(--success)',
                            fontWeight: 600,
                          }}
                        >
                          {amt}
                        </span>
                        {t.partyName ? <> · {t.partyName}</> : null}
                      </div>
                    </div>
                    <div className="attn-action">
                      <button
                        className="btn-action"
                        type="button"
                        style={{
                          color: '#D97706',
                          borderColor: '#FDE68A',
                          background: '#FFFBEB',
                        }}
                        onClick={() => onOpenTransaction(t.id)}
                      >
                        Review
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Client Payments */}
        <div className="card" style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-primary)',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <i className="ph-bold ph-wallet" style={{ color: 'var(--brand)', fontSize: 16 }} />{' '}
            Client Payments
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Total Project Budget
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              ₹{formatInrFull(budget)}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Payments Received
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>
              ₹{formatInrFull(totals.income)}
            </div>
          </div>

          <div style={{ height: 1, background: 'var(--border)', marginBottom: 16 }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
              Balance Pending
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--critical)' }}>
              ₹{formatInrFull(balancePending)}
            </div>
          </div>

          <div
            style={{
              width: '100%',
              height: 6,
              background: '#F3F4F6',
              borderRadius: 3,
              overflow: 'hidden',
              marginBottom: 8,
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${collectedPct}%`,
                background: 'var(--success)',
                borderRadius: 3,
              }}
            />
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--text-tertiary)',
              textAlign: 'right',
            }}
          >
            {collectedPct}% Collected
          </div>
        </div>
      </div>

      {/* ─── Advance slide-overs ─── */}
      {/* Mounted at the page level so they survive parent re-renders
          (the live snapshot above re-runs them, but the open/close
          state outlives that). Both render-as-null when their
          target advance is null. */}
      <AddAdvanceBillSlideOver
        open={!!addBillTo}
        advance={addBillTo}
        onClose={() => setAddBillTo(null)}
      />
      <CloseAdvanceSlideOver
        open={!!closeAdvanceFor}
        advance={closeAdvanceFor}
        onClose={() => setCloseAdvanceFor(null)}
      />
    </div>
  );
}

// ───── Row sub-component ─────

function TxnRow({ txn, onClick }: { txn: TransactionItem; onClick: () => void }) {
  const kind = normaliseTxType(txn.type);
  const ms = txDateMs(txn);
  const isPending = txn.workflowStatus === 'pending_approval';
  const isRejected = txn.workflowStatus === 'rejected';
  const partyName = txn.partyName?.trim() || 'Unknown';
  const colors = pickAvatarColors(partyName);

  const amountColor = isRejected
    ? 'var(--text-tertiary)'
    : isPending
      ? 'var(--text-primary)'
      : kind === 'out'
        ? 'var(--critical)'
        : kind === 'in'
          ? 'var(--success)'
          : 'var(--text-primary)';

  const amountPrefix = kind === 'out' ? '-' : kind === 'in' ? '+' : '';

  return (
    <tr
      className={`dt-row${isPending ? ' pending' : ''}${isRejected ? ' rejected' : ''}`}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <td style={{ padding: '12px 16px', verticalAlign: 'middle', width: 100 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {dayShort(ms)}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{yearOf(ms)}</div>
      </td>
      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: isRejected ? '#F3F4F6' : colors.bg,
              color: isRejected ? 'var(--text-tertiary)' : colors.fg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {partyInitials(partyName)}
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: isRejected ? 'var(--text-secondary)' : 'var(--text-primary)',
                marginBottom: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {partyName}
              {isPending ? (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    background: '#FDE68A',
                    color: '#92400E',
                    padding: '2px 6px',
                    borderRadius: 10,
                  }}
                >
                  PENDING
                </span>
              ) : isRejected ? (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    background: '#FECACA',
                    color: '#991B1B',
                    padding: '2px 6px',
                    borderRadius: 10,
                  }}
                >
                  REJECTED
                </span>
              ) : null}
            </div>
            <div
              style={{
                fontSize: 12,
                color: isRejected ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                textDecoration: isRejected ? 'line-through' : undefined,
              }}
            >
              {txn.description?.trim() || '—'}
            </div>
          </div>
        </div>
      </td>
      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: isRejected ? 'var(--text-tertiary)' : 'var(--text-secondary)',
            background: '#F3F4F6',
            padding: '4px 8px',
            borderRadius: 6,
          }}
        >
          {categoryLabel(txn.category as TransactionCategory)}
        </span>
      </td>
      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: isRejected ? 'var(--text-secondary)' : 'var(--text-primary)',
          }}
        >
          {paymentMethodLabel(txn.paymentMethod as PaymentMethod)}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          {txn.referenceNumber?.trim() || 'N/A'}
        </div>
      </td>
      <td style={{ padding: '12px 16px', verticalAlign: 'middle', textAlign: 'right' }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: amountColor,
            fontVariantNumeric: 'tabular-nums',
            textDecoration: isRejected ? 'line-through' : undefined,
          }}
        >
          {amountPrefix}
          {formatInrFull(txn.amount ?? 0)}
        </div>
      </td>
      <td style={{ padding: '12px 16px', verticalAlign: 'middle', textAlign: 'right' }}>
        <button
          className="btn-ghost"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          aria-label="Open transaction"
        >
          <i className="ph-bold ph-caret-right" />
        </button>
      </td>
    </tr>
  );
}

// ───── Advance parent row ─────
//
// Renders a `staff_advance` parent transaction with:
//   - Advance-status pill (OPEN / PARTIAL / SETTLED)
//   - Subline showing remaining balance + bills-filed count
//   - Chevron toggle that reveals indented child rows
//   - Per-row actions: Add bill · Close advance
//
// Each child row is a normal-looking <tr> indented and tagged
// with "↳ Bill against advance" so the audit trail is obvious.

function AdvanceParentRow({
  txn,
  expanded,
  onToggle,
  onOpen,
  onAddBill,
  onClose,
}: {
  txn: TransactionItem;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onAddBill: () => void;
  onClose: () => void;
}) {
  const ms = txDateMs(txn);
  const partyName = txn.partyName?.trim() || 'Unknown';
  const colors = pickAvatarColors(partyName);
  const tone = advanceStatusTone(txn.advanceStatus);
  const label = advanceStatusLabel(txn.advanceStatus);
  const isSettled = txn.advanceStatus === 'settled';

  // Live children — fetched inline per parent row. This is one
  // extra Firestore listener per displayed advance row (which is
  // typically <5 per project page) — fine. The alternative would
  // be a single useProjectTransactions read for the whole tree
  // and grouping client-side, which we already have for the
  // main list, but routing it through here would duplicate the
  // filter logic. Keeping it simple.
  const { children } = useAdvanceChildren(txn.orgId, txn.id);
  const live = useMemo(
    () =>
      computeAdvanceBalance(
        { amount: txn.amount ?? 0, advanceStatus: txn.advanceStatus },
        children.map((c) => ({
          type: c.type,
          amount: c.amount ?? 0,
          advanceRole: c.advanceRole,
          workflowStatus: c.workflowStatus,
        })),
      ),
    [txn.amount, txn.advanceStatus, children],
  );

  const balanceLabel =
    live.balance > 0
      ? `₹${formatInrFull(live.balance)} unsettled`
      : live.balance < 0
        ? `₹${formatInrFull(Math.abs(live.balance))} over · needs reimbursement`
        : isSettled
          ? 'Settled · ₹0 balance'
          : '₹0 balance · ready to close';

  return (
    <>
      <tr
        className="dt-row"
        style={{
          cursor: 'pointer',
          borderBottom: '1px solid var(--border)',
          background: '#FFFBEB',
        }}
        onClick={onToggle}
      >
        <td style={{ padding: '12px 16px', verticalAlign: 'middle', width: 100 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            {dayShort(ms)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{yearOf(ms)}</div>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className="btn-ghost"
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              aria-label={expanded ? 'Collapse advance' : 'Expand advance'}
              style={{ padding: 4 }}
            >
              <i
                className={`ph-bold ${expanded ? 'ph-caret-down' : 'ph-caret-right'}`}
              />
            </button>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: colors.bg,
                color: colors.fg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              <i className="ph-bold ph-wallet" />
            </div>
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {partyName}
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    background: tone.bg,
                    color: tone.fg,
                    padding: '2px 6px',
                    borderRadius: 10,
                    letterSpacing: 0.4,
                  }}
                >
                  {label}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {balanceLabel} · {children.length} bill
                {children.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--text-secondary)',
              background: '#F3F4F6',
              padding: '4px 8px',
              borderRadius: 6,
            }}
          >
            Staff Advance
          </span>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {!isSettled ? (
              <>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddBill();
                  }}
                  style={{ fontSize: 11, padding: '4px 8px' }}
                >
                  <i className="ph ph-plus" /> Bill
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  style={{ fontSize: 11, padding: '4px 8px' }}
                  title="Close advance"
                >
                  <i className="ph ph-check-circle" /> Close
                </button>
              </>
            ) : (
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>—</span>
            )}
          </div>
        </td>
        <td
          style={{
            padding: '12px 16px',
            verticalAlign: 'middle',
            textAlign: 'right',
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--critical)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            -{formatInrFull(txn.amount ?? 0)}
          </div>
        </td>
        <td
          style={{
            padding: '12px 16px',
            verticalAlign: 'middle',
            textAlign: 'right',
          }}
        >
          <button
            className="btn-ghost"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            aria-label="Open advance detail"
          >
            <i className="ph-bold ph-caret-right" />
          </button>
        </td>
      </tr>
      {expanded && children.length > 0
        ? children
            .slice()
            .sort((a, b) => txDateMs(a) - txDateMs(b))
            .map((child) => (
              <AdvanceChildRow
                key={child.id}
                child={child}
                onClick={() => onOpen()}
                onUnapply={
                  child.advanceRole === 'settlement' && !isSettled
                    ? async () => {
                        // Finance-tier action: undo a wrong link.
                        // The bill stays as a free-standing
                        // payment_out; parent status recomputes.
                        // Confirm before flip — accidental
                        // clicks on a bill row would otherwise
                        // silently break the audit trail.
                        // eslint-disable-next-line no-alert
                        const ok = window.confirm(
                          `Unlink this ₹${formatInrFull(child.amount ?? 0)} bill from the advance?\n\nThe bill will remain on the project as a standalone expense, but it will stop reducing the advance wallet. You can re-link it later by editing the transaction.`,
                        );
                        if (!ok) return;
                        try {
                          await unapplyAdvanceSettlement(child.id);
                        } catch (e) {
                          // eslint-disable-next-line no-alert
                          window.alert(
                            `Couldn't unlink: ${e instanceof Error ? e.message : String(e)}`,
                          );
                        }
                      }
                    : undefined
                }
              />
            ))
        : null}
    </>
  );
}

function AdvanceChildRow({
  child,
  onClick,
  onUnapply,
}: {
  child: TransactionItem;
  onClick: () => void;
  /** When provided, surfaces an "Unlink" button in the actions
   *  column. Only passed when the child is a SETTLEMENT (refunds
   *  + reimbursements stay linked — those operations only make
   *  sense in the advance context). */
  onUnapply?: () => void;
}) {
  const kind = normaliseTxType(child.type);
  const ms = txDateMs(child);
  const role = child.advanceRole;
  const isRefund = role === 'refund' || kind === 'in';
  const isReimburse = role === 'reimbursement';
  const amountColor = isRefund ? 'var(--success)' : 'var(--text-primary)';
  const amountPrefix = isRefund ? '+' : '-';
  const roleTag =
    role === 'refund'
      ? 'Refund'
      : role === 'reimbursement'
        ? 'Reimbursement'
        : 'Bill';

  return (
    <tr
      style={{
        cursor: 'pointer',
        borderBottom: '1px solid var(--border)',
        background: '#FAFAFA',
      }}
      onClick={onClick}
    >
      <td style={{ padding: '8px 16px', verticalAlign: 'middle', width: 100 }}>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', paddingLeft: 20 }}>
          ↳ {dayShort(ms)}
        </div>
      </td>
      <td style={{ padding: '8px 16px', verticalAlign: 'middle' }}>
        <div style={{ paddingLeft: 52, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {child.partyName?.trim() || 'Unknown'}
          </span>
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              background: isRefund
                ? '#DCFCE7'
                : isReimburse
                  ? '#FEE2E2'
                  : '#F3F4F6',
              color: isRefund
                ? '#15803D'
                : isReimburse
                  ? '#991B1B'
                  : 'var(--text-tertiary)',
              padding: '2px 6px',
              borderRadius: 8,
              letterSpacing: 0.3,
            }}
          >
            {roleTag.toUpperCase()}
          </span>
          {child.description?.trim() ? (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              · {child.description}
            </span>
          ) : null}
        </div>
      </td>
      <td style={{ padding: '8px 16px', verticalAlign: 'middle' }}>
        <span
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
          }}
        >
          {categoryLabel(child.category as TransactionCategory)}
        </span>
      </td>
      <td style={{ padding: '8px 16px', verticalAlign: 'middle' }}>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
          {paymentMethodLabel(child.paymentMethod as PaymentMethod)}
        </span>
      </td>
      <td
        style={{
          padding: '8px 16px',
          verticalAlign: 'middle',
          textAlign: 'right',
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: amountColor,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {amountPrefix}
          {formatInrFull(child.amount ?? 0)}
        </div>
      </td>
      <td
        style={{
          padding: '8px 16px',
          verticalAlign: 'middle',
          textAlign: 'right',
        }}
      >
        {onUnapply ? (
          <button
            type="button"
            className="btn-secondary"
            onClick={(e) => {
              e.stopPropagation();
              void onUnapply();
            }}
            style={{
              fontSize: 10,
              padding: '3px 8px',
              color: 'var(--text-secondary)',
            }}
            title="Unlink this bill from the advance (it stays as a standalone expense)"
          >
            <i className="ph ph-link-break" /> Unlink
          </button>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>—</span>
        )}
      </td>
    </tr>
  );
}

// ───── Sidebar sub-components ─────

function KpiCard({
  label,
  value,
  borderColor,
}: {
  label: string;
  value: number;
  borderColor: string;
}) {
  const compact = compactInr(value);
  const suffix = compact.match(/[KLM]r?$/i)?.[0] ?? '';
  const head = suffix ? compact.slice(0, -suffix.length) : compact;
  return (
    <div
      className="card"
      style={{
        padding: '12px 16px',
        borderBottom: `3px solid ${borderColor}`,
        flex: 1,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        ₹{head}
        {suffix ? (
          <span style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>{suffix}</span>
        ) : null}
      </div>
    </div>
  );
}

// ───── Filters popover ─────

function FiltersButton({
  activeCount,
  category,
  method,
  onCategory,
  onMethod,
}: {
  activeCount: number;
  category: TransactionCategory | 'all';
  method: PaymentMethod | 'all';
  onCategory: (v: TransactionCategory | 'all') => void;
  onMethod: (v: PaymentMethod | 'all') => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn-secondary"
        style={{ height: 34, background: '#FAFAFA' }}
        onClick={() => setOpen((o) => !o)}
      >
        <i className="ph ph-sliders-horizontal" /> Filters
        {activeCount > 0 ? (
          <span
            style={{
              background: 'var(--brand)',
              color: '#fff',
              fontSize: 10,
              width: 18,
              height: 18,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              marginLeft: 4,
            }}
          >
            {activeCount}
          </span>
        ) : null}
      </button>
      {open ? (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 30 }}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 4px)',
              minWidth: 240,
              background: '#fff',
              border: '1px solid var(--border)',
              borderRadius: 8,
              boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
              zIndex: 31,
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                Category
              </div>
              <select
                value={category}
                onChange={(e) => onCategory(e.currentTarget.value as TransactionCategory | 'all')}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 13,
                  background: '#fff',
                  fontFamily: 'inherit',
                }}
              >
                <option value="all">All categories</option>
                <option value="labour">Labour</option>
                <option value="material">Material</option>
                <option value="transport">Transport</option>
                <option value="equipment">Equipment</option>
                <option value="sub_contractor">Sub Contractor</option>
                <option value="customer">Customer</option>
                <option value="designer">Designer</option>
                <option value="food_and_travel">Food &amp; Travel</option>
                <option value="fuel">Fuel</option>
                <option value="salary">Salary</option>
                <option value="rent">Rent</option>
                <option value="others">Others</option>
              </select>
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                Payment method
              </div>
              <select
                value={method}
                onChange={(e) => onMethod(e.currentTarget.value as PaymentMethod | 'all')}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 13,
                  background: '#fff',
                  fontFamily: 'inherit',
                }}
              >
                <option value="all">All methods</option>
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="upi">UPI</option>
              </select>
            </div>
            {(category !== 'all' || method !== 'all') ? (
              <button
                type="button"
                className="btn-ghost"
                style={{ fontSize: 12, color: 'var(--brand)', alignSelf: 'flex-start' }}
                onClick={() => {
                  onCategory('all');
                  onMethod('all');
                }}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
